import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { apps } from "../data/apps";
import { isLiveOnPlay, ratingLabel, installsLabel } from "../data/appStats";
import shotsMap from "../data/screenshots.json";
import "../App.css";

// Safe text truncation: find last space before limit, avoid mid-word cuts
const safeTruncate = (text: string, limit: number = 160): string => {
  if (!text || text.length <= limit) return text;
  const truncated = text.slice(0, limit);
  const lastSpace = truncated.lastIndexOf(" ");
  return lastSpace > 0 ? truncated.slice(0, lastSpace) + "..." : truncated + "...";
};

// Sticky bottom install bar — appears on mobile once the hero CTA scrolls away,
// so the Play Store is always one tap away.
const StickyInstallBar: React.FC<{ app: any; accent: string }> = ({ app, accent }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 560);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const r = ratingLabel(app.id);
  if (!show) return null;
  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000,
      display: "flex", alignItems: "center", gap: "0.75rem",
      padding: "0.65rem 1rem calc(0.65rem + env(safe-area-inset-bottom))",
      background: "rgba(251, 250, 246, 0.94)", backdropFilter: "blur(14px)",
      borderTop: "1px solid #E1DCCE",
    }}>
      {app.icon && <img src={app.icon} alt="" style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid #E1DCCE" }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "#26313B", fontWeight: 700, fontSize: "0.92rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{app.name}</div>
        <div style={{ color: "#5B6B7C", fontSize: "0.75rem" }}>
          {r ? <>★ {r.rating.toFixed(1)} · </> : null}Free on Google Play
        </div>
      </div>
      <a href={app.playstore} rel="noopener noreferrer"
        onClick={(e: any) => { e.preventDefault(); (window as any).gtag_report_conversion?.(app.playstore) || window.open(app.playstore, "_blank"); }}
        style={{ flex: "none", display: "inline-flex", alignItems: "center", gap: "0.45rem", padding: "0.7rem 1.3rem", background: "#E8B44C", border: "1px solid #D9A32E", color: "#221A06", borderRadius: "0.8rem", fontWeight: 700, fontSize: "0.9rem", textDecoration: "none", boxShadow: "0 1px 2px rgba(52,44,26,.06), 0 4px 14px rgba(52,44,26,.05)" }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.2l2.807 1.626a1 1 0 010 1.734l-2.808 1.626L15.206 12l2.492-2.493zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" /></svg>
        Install
      </a>
    </div>
  );
};

// Extract an 11-char YouTube id from watch/share/embed/shorts URLs.
const youtubeId = (url?: string): string | null => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : null;
};

// Hero media: a muted, looping, autoplaying promo when the app has a YouTube
// link, mounted lazily (only once scrolled near view) so it never hurts the
// initial page load / Core Web Vitals. Falls back to the photoreal hero image.
const AppHeroMedia: React.FC<{ youtube?: string; poster?: string; name: string }> = ({ youtube, poster, name }) => {
  const id = youtubeId(youtube);
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!id || !ref.current) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) { setShow(true); io.disconnect(); }
    }, { rootMargin: "250px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [id]);

  if (!id) {
    if (!poster) return null;
    return <img src={poster} alt={`${name} app screen`} style={{ width: "100%", height: "auto", display: "block" }} loading="eager" />;
  }
  return (
    <div ref={ref} style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", background: "#F1EEE5" }}>
      {show ? (
        <iframe
          title={`${name} promo video`}
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=1&modestbranding=1&rel=0&playsinline=1`}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          loading="lazy"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
        />
      ) : (
        poster && <img src={poster} alt={`${name} promo`} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
      )}
    </div>
  );
};

const AppPromoPage: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const app = apps.find((a: any) => a.id === appId);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  if (!app) return <Navigate to="/" replace />;

  const live = isLiveOnPlay(app.id);
  const rating = ratingLabel(app.id);
  const installs = installsLabel(app.id);
  const otherApps = apps.filter((a: any) => a.id !== app.id && isLiveOnPlay(a.id));
  const accent = "#A8720E"; // warm-paper light theme: flat amber accent for all apps
  const shots: string[] = (app as any).screenshots?.length ? (app as any).screenshots : ((shotsMap as any)[app.id] || []);

  const accentColorMap: Record<string, { bg: string; border: string; light: string }> = {
    "#06b6d4": { bg: "#F1EEE5", border: "#E1DCCE", light: "#A8720E" },
    "#10b981": { bg: "#F1EEE5", border: "#E1DCCE", light: "#A8720E" },
    "#f97316": { bg: "#F1EEE5", border: "#E1DCCE", light: "#A8720E" },
    "#8b5cf6": { bg: "#F1EEE5", border: "#E1DCCE", light: "#A8720E" },
    "#f472b6": { bg: "#F1EEE5", border: "#E1DCCE", light: "#A8720E" },
    "#60a5fa": { bg: "#F1EEE5", border: "#E1DCCE", light: "#A8720E" },
  };
  const colors = accentColorMap[accent] || { bg: "#F1EEE5", border: "#E1DCCE", light: "#A8720E" };

  const faqSchema = app.faqs?.map((faq: any) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: { "@type": "Answer", text: faq.a },
  }));

  return (
    <div className="app">
      <Helmet>
        <title>{`${app.name} — ${app.tagline} | Download Free for Android`}</title>
        <meta name="title" content={`${app.name} — ${app.tagline} | Download Free for Android`} />
        <meta name="description" content={safeTruncate(app.longDescription || app.description, 160)} />
        <meta name="keywords" content={`${app.name}, ${app.category}, ${app.features?.join(", ")}, Cosmovex, Android app, developer tools, free ${app.category.toLowerCase()} app, mobile ${app.category.toLowerCase()}`} />
        <link rel="canonical" href={`https://cosmovexapps.com/apps/${app.id}`} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://cosmovexapps.com/apps/${app.id}`} />
        <meta property="og:title" content={`${app.name} - ${app.tagline}`} />
        <meta property="og:description" content={app.description} />
        <meta property="og:image" content="https://cosmovexapps.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`${app.name} - ${app.category} app by Cosmovex`} />
        <meta property="og:site_name" content="Cosmovex" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${app.name} - ${app.tagline}`} />
        <meta name="twitter:description" content={app.description} />
        <meta name="twitter:image" content={`https://cosmovexapps.com${app.icon || "/og-image.png"}`} />
        <meta name="twitter:image:alt" content={`${app.name} - ${app.category} app by Cosmovex`} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            name: app.name,
            description: app.longDescription || app.description,
            operatingSystem: app.appstore && app.playstore ? "Android, iOS" : app.appstore ? "iOS" : "Android",
            applicationCategory: "DeveloperApplication",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            url: `https://cosmovexapps.com/apps/${app.id}`,
            installUrl: app.playstore,
            ...(app.heroImage && { image: `https://cosmovexapps.com${app.heroImage}` }),
            ...(shots.length && { screenshot: shots.map((s) => `https://cosmovexapps.com${s}`) }),
            author: {
              "@type": "Organization",
              name: "Cosmovex",
              url: "https://cosmovexapps.com",
            },
            ...(rating && {
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: rating.rating,
                ratingCount: rating.count,
                bestRating: 5,
                worstRating: 1,
              },
            }),
          })}
        </script>

        {faqSchema && faqSchema.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqSchema,
            })}
          </script>
        )}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://cosmovexapps.com/" },
              { "@type": "ListItem", position: 2, name: "Apps", item: "https://cosmovexapps.com/apps" },
              { "@type": "ListItem", position: 3, name: app.name, item: `https://cosmovexapps.com/apps/${app.id}` },
            ],
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="header">
        <Link to="/" className="flex items-center gap-3 group">
          <img src="/logo192.png" alt="" style={{ width: 40, height: 40, borderRadius: 12, objectFit: "cover" }} />
          <div>
            <h1 className="text-lg font-bold text-[#26313B]">Cosmovex</h1>
            <p className="text-xs text-[#8A93A2] font-mono">// Developer Tools</p>
          </div>
        </Link>
        <nav className="nav">
          <Link to="/">Home</Link>
          <a href="#features">Features</a>
          <a href="#faq">FAQ</a>
          <Link to="/about">About</Link>
        </nav>
      </header>

      <main className="main">
        {/* ── HERO ── */}
        <section style={{ padding: "6rem 2rem 4rem", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, background: "none", pointerEvents: "none" }} />
          <div style={{ position: "absolute", top: "-20%", left: "50%", transform: "translateX(-50%)", width: "700px", height: "500px", background: "none", pointerEvents: "none" }} />

          <div style={{ maxWidth: "900px", margin: "0 auto", position: "relative", zIndex: 10 }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              {/* Breadcrumb */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "2rem", fontSize: "0.85rem" }}>
                <Link to="/" style={{ color: "#0F6BBF", textDecoration: "none" }}>Home</Link>
                <span style={{ color: "#8A93A2" }}>/</span>
                <Link to="/apps" style={{ color: "#0F6BBF", textDecoration: "none" }}>Apps</Link>
                <span style={{ color: "#8A93A2" }}>/</span>
                <span style={{ color: accent }}>{app.name}</span>
              </div>

              {/* App header */}
              <div style={{ display: "flex", alignItems: "center", gap: "1.25rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
                {app.icon ? (
                  <img src={app.icon} alt={`${app.name} app icon`} style={{ width: "80px", height: "80px", borderRadius: "1.25rem", border: "1px solid #E1DCCE" }} />
                ) : (
                  <div style={{ width: "80px", height: "80px", borderRadius: "1.25rem", background: colors.bg, border: `1px solid ${colors.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.25rem", fontWeight: 800, color: accent }}>{app.name[0]}</div>
                )}
                <div>
                  <h2 style={{ fontSize: "clamp(2rem, 5vw, 3rem)", fontWeight: 800, color: "#26313B", fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "-0.02em", lineHeight: 1.1 }}>{app.name}</h2>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
                    <span style={{ padding: "0.3rem 0.8rem", borderRadius: "0.5rem", background: colors.bg, border: `1px solid ${colors.border}`, color: accent, fontSize: "0.8rem", fontWeight: 600 }}>{app.category}</span>
                    {rating && (
                      <span style={{ color: "#26313B", fontSize: "0.9rem", fontWeight: 700 }}>
                        <span style={{ color: "#A8720E" }}>★</span> {rating.rating.toFixed(1)}
                        <span style={{ color: "#5B6B7C", fontWeight: 500 }}> ({rating.count})</span>
                      </span>
                    )}
                    {installs && <span style={{ color: "#5B6B7C", fontSize: "0.9rem" }}>{installs}</span>}
                    <span style={{ color: "#5B6B7C", fontSize: "0.9rem" }}>by Cosmovex</span>
                  </div>
                </div>
              </div>

              <p style={{ fontSize: "clamp(1.1rem, 2vw, 1.3rem)", color: accent, fontWeight: 600, marginBottom: "1rem" }}>{app.tagline}</p>
              <p style={{ fontSize: "1.1rem", color: "#3D4B59", lineHeight: 1.8, marginBottom: "2rem", maxWidth: "750px" }}>{app.description}</p>

              {/* Feature pills */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", marginBottom: "2.5rem" }}>
                {app.features?.map((f: string, i: number) => (
                  <span key={i} style={{ padding: "0.5rem 1rem", borderRadius: "0.75rem", background: colors.bg, border: `1px solid ${colors.border}`, color: colors.light, fontSize: "0.9rem", fontWeight: 600 }}>{f}</span>
                ))}
              </div>

              {/* CTAs */}
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                {live && <motion.a href={app.playstore} target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  onClick={(e: any) => { e.preventDefault(); (window as any).gtag_report_conversion?.(app.playstore) || window.open(app.playstore, '_blank'); }}
                  style={{ display: "inline-flex", alignItems: "center", gap: "0.6rem", padding: "1rem 2rem", background: "#E8B44C", border: "1px solid #D9A32E", color: "#221A06", borderRadius: "1rem", fontWeight: 700, fontSize: "1.05rem", textDecoration: "none", boxShadow: "0 1px 2px rgba(52,44,26,.06), 0 4px 14px rgba(52,44,26,.05)" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.2l2.807 1.626a1 1 0 010 1.734l-2.808 1.626L15.206 12l2.492-2.493zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" /></svg>
                  Download on Google Play
                </motion.a>}
                {app.appstore && (
                  <motion.a href={app.appstore} target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                    onClick={(e: any) => { e.preventDefault(); (window as any).gtag_report_conversion?.(app.appstore) || window.open(app.appstore, '_blank'); }}
                    style={{ display: "inline-flex", alignItems: "center", gap: "0.6rem", padding: "1rem 2rem", background: "#FFFFFF", border: "1px solid #E1DCCE", color: "#26313B", borderRadius: "1rem", fontWeight: 700, fontSize: "1.05rem", textDecoration: "none", boxShadow: "0 1px 2px rgba(52,44,26,.06), 0 4px 14px rgba(52,44,26,.05)" }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    Download on App Store
                  </motion.a>
                )}
                <Link to={`/apps/${app.id}/privacy-policy`}
                  style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", padding: "1rem 1.75rem", background: "#FFFFFF", border: "1px solid #E1DCCE", color: "#3D4B59", borderRadius: "1rem", fontWeight: 600, textDecoration: "none" }}>
                  Privacy Policy
                </Link>
              </div>

              {/* Hero media — promo video (muted autoplay loop, lazy) or hero image */}
              {((app as any).youtube || app.heroImage) && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
                  style={{ marginTop: "3rem", borderRadius: "1.25rem", overflow: "hidden", border: `1px solid ${colors.border}`, boxShadow: "0 1px 2px rgba(52,44,26,.06), 0 20px 60px rgba(52,44,26,.14)" }}>
                  <AppHeroMedia youtube={(app as any).youtube} poster={app.heroImage} name={app.name} />
                </motion.div>
              )}
            </motion.div>
          </div>
        </section>

        {/* ── ABOUT ── */}
        {app.longDescription && (
          <section style={{ padding: "4rem 2rem", background: "#F1EEE5" }}>
            <div style={{ maxWidth: "800px", margin: "0 auto" }}>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <h2 style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)", fontWeight: 800, color: "#26313B", fontFamily: "'Space Grotesk', sans-serif", marginBottom: "1.5rem" }}>
                  About {app.name}
                </h2>
                <p style={{ color: "#3D4B59", fontSize: "1.05rem", lineHeight: 1.9 }}>{app.longDescription}</p>
              </motion.div>
            </div>
          </section>
        )}

        {/* ── SCREENSHOTS ── */}
        {shots.length > 0 && (
          <section style={{ padding: "3.5rem 0 1rem" }}>
            <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 2rem" }}>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: "2rem" }}>
                <span style={{ display: "inline-block", fontSize: "0.8rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em", color: accent, marginBottom: "0.75rem" }}>Screenshots</span>
                <h2 style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)", fontWeight: 800, color: "#26313B", fontFamily: "'Space Grotesk', sans-serif" }}>See it in action</h2>
              </motion.div>
            </div>
            <div style={{ display: "flex", gap: "1rem", overflowX: "auto", padding: "0.5rem 2rem 1.5rem", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" }}>
              {shots.map((src: string, i: number) => (
                <img key={i} src={src} alt={`${app.name} screenshot ${i + 1}`} loading="lazy"
                  style={{ height: "clamp(380px, 60vh, 540px)", width: "auto", borderRadius: "1.25rem", border: `1px solid ${colors.border}`, flexShrink: 0, scrollSnapAlign: "center", boxShadow: "0 14px 44px rgba(52,44,26,.14)" }} />
              ))}
            </div>
          </section>
        )}

        {/* ── FEATURES ── */}
        {app.detailedFeatures && (
          <section id="features" style={{ padding: "5rem 2rem" }}>
            <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: "3.5rem" }}>
                <span style={{ display: "inline-block", fontSize: "0.8rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em", color: accent, marginBottom: "0.75rem" }}>Features</span>
                <h2 style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)", fontWeight: 800, color: "#26313B", fontFamily: "'Space Grotesk', sans-serif" }}>Everything You Need</h2>
              </motion.div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1.5rem" }}>
                {app.detailedFeatures.map((feat: any, i: number) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ delay: 0.08 * i, duration: 0.5 }}
                    style={{ padding: "2rem", borderRadius: "1.25rem", background: "#FFFFFF", border: "1px solid #E1DCCE", boxShadow: "0 1px 2px rgba(52,44,26,.06), 0 4px 14px rgba(52,44,26,.05)", transition: "border-color 0.3s" }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#D9A32E")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E1DCCE")}>
                    <div style={{ width: "48px", height: "48px", borderRadius: "0.75rem", background: colors.bg, border: `1px solid ${colors.border}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.25rem" }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={feat.icon} /></svg>
                    </div>
                    <h3 style={{ fontSize: "1.15rem", fontWeight: 700, color: "#26313B", marginBottom: "0.5rem" }}>{feat.title}</h3>
                    <p style={{ color: "#5B6B7C", fontSize: "0.9rem", lineHeight: 1.7 }}>{feat.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── CTA BANNER ── */}
        <section style={{ padding: "4rem 2rem", background: "#F1EEE5" }}>
          <div style={{ maxWidth: "700px", margin: "0 auto", textAlign: "center" }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)", fontWeight: 800, color: "#26313B", fontFamily: "'Space Grotesk', sans-serif", marginBottom: "1rem" }}>
                Ready to Try {app.name}?
              </h2>
              <p style={{ color: "#3D4B59", fontSize: "1.1rem", marginBottom: "2rem", lineHeight: 1.7 }}>
                {live
                  ? `Free to download. No account required. Available on Android${app.appstore ? " and iOS" : ""}.`
                  : "This app is currently unavailable on Google Play. Check out our other apps below."}
              </p>
              <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
                {live && <motion.a href={app.playstore} target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  onClick={(e: any) => { e.preventDefault(); (window as any).gtag_report_conversion?.(app.playstore) || window.open(app.playstore, '_blank'); }}
                  style={{ display: "inline-flex", alignItems: "center", gap: "0.6rem", padding: "1rem 2rem", background: "#E8B44C", border: "1px solid #D9A32E", color: "#221A06", borderRadius: "1rem", fontWeight: 700, fontSize: "1.05rem", textDecoration: "none", boxShadow: "0 1px 2px rgba(52,44,26,.06), 0 4px 14px rgba(52,44,26,.05)" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.2l2.807 1.626a1 1 0 010 1.734l-2.808 1.626L15.206 12l2.492-2.493zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" /></svg>
                  Google Play
                </motion.a>}
                {app.appstore && (
                  <motion.a href={app.appstore} target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                    onClick={(e: any) => { e.preventDefault(); (window as any).gtag_report_conversion?.(app.appstore) || window.open(app.appstore, '_blank'); }}
                    style={{ display: "inline-flex", alignItems: "center", gap: "0.6rem", padding: "1rem 2rem", background: "#FFFFFF", border: "1px solid #E1DCCE", color: "#26313B", borderRadius: "1rem", fontWeight: 700, fontSize: "1.05rem", textDecoration: "none", boxShadow: "0 1px 2px rgba(52,44,26,.06), 0 4px 14px rgba(52,44,26,.05)" }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    App Store
                  </motion.a>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── FAQ ── */}
        {app.faqs && app.faqs.length > 0 && (
          <section id="faq" style={{ padding: "5rem 2rem" }}>
            <div style={{ maxWidth: "750px", margin: "0 auto" }}>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: "3rem" }}>
                <span style={{ display: "inline-block", fontSize: "0.8rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em", color: accent, marginBottom: "0.75rem" }}>FAQ</span>
                <h2 style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)", fontWeight: 800, color: "#26313B", fontFamily: "'Space Grotesk', sans-serif" }}>Frequently Asked Questions</h2>
              </motion.div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {app.faqs.map((faq: any, i: number) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.05 * i }}>
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      style={{ width: "100%", textAlign: "left", padding: "1.25rem 1.5rem", borderRadius: openFaq === i ? "1rem 1rem 0 0" : "1rem", background: "#FFFFFF", border: `1px solid ${openFaq === i ? "#D9A32E" : "#E1DCCE"}`, borderBottom: openFaq === i ? "none" : undefined, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
                      <span style={{ color: "#26313B", fontWeight: 600, fontSize: "1rem" }}>{faq.q}</span>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ flexShrink: 0, transform: openFaq === i ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.25s" }}>
                        <path d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openFaq === i && (
                      <div style={{ padding: "1rem 1.5rem 1.25rem", borderRadius: "0 0 1rem 1rem", background: "#FFFFFF", border: "1px solid #D9A32E", borderTop: "none" }}>
                        <p style={{ color: "#3D4B59", lineHeight: 1.7, fontSize: "0.95rem" }}>{faq.a}</p>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── MORE APPS ── */}
        <section style={{ padding: "5rem 2rem", background: "#F1EEE5" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: "2.5rem" }}>
              <h2 style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)", fontWeight: 800, color: "#26313B", fontFamily: "'Space Grotesk', sans-serif", marginBottom: "0.5rem" }}>More from Cosmovex</h2>
              <p style={{ color: "#5B6B7C", fontSize: "1rem" }}>Explore our other developer tools</p>
            </motion.div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.25rem" }}>
              {otherApps.map((other: any, i: number) => {
                const otherColors = accentColorMap[other.accentHex] || { bg: "#F1EEE5", border: "#E1DCCE", light: "#A8720E" };
                return (
                  <motion.div key={other.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 * i }}
                    style={{ padding: "1.5rem", borderRadius: "1.25rem", background: "#FFFFFF", border: "1px solid #E1DCCE", boxShadow: "0 1px 2px rgba(52,44,26,.06), 0 4px 14px rgba(52,44,26,.05)", cursor: "pointer", transition: "border-color 0.3s" }}
                    onClick={() => window.location.href = `/apps/${other.id}`}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#D9A32E")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E1DCCE")}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.85rem", marginBottom: "0.75rem" }}>
                      {other.icon ? (
                        <img src={other.icon} alt={other.name} style={{ width: "44px", height: "44px", borderRadius: "0.7rem", border: "1px solid #E1DCCE" }} />
                      ) : (
                        <div style={{ width: "44px", height: "44px", borderRadius: "0.7rem", background: otherColors.bg, border: `1px solid ${otherColors.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem", fontWeight: 800, color: "#A8720E" }}>{other.name[0]}</div>
                      )}
                      <div>
                        <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#26313B" }}>{other.name}</h3>
                        <span style={{ fontSize: "0.75rem", color: "#A8720E", fontWeight: 600 }}>{other.category}</span>
                      </div>
                    </div>
                    <p style={{ color: "#5B6B7C", fontSize: "0.85rem", lineHeight: 1.6 }}>{other.tagline}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer style={{ padding: "2rem", borderTop: "1px solid #E1DCCE", background: "#FBFAF6", textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", gap: "2rem", flexWrap: "wrap", marginBottom: "1rem" }}>
          <Link to="/" style={{ color: "#5B6B7C", textDecoration: "none", fontSize: "0.9rem" }}>Home</Link>
          <Link to="/about" style={{ color: "#5B6B7C", textDecoration: "none", fontSize: "0.9rem" }}>About</Link>
          <Link to="/blogs" style={{ color: "#5B6B7C", textDecoration: "none", fontSize: "0.9rem" }}>Blog</Link>
          <Link to="/privacy-policy" style={{ color: "#5B6B7C", textDecoration: "none", fontSize: "0.9rem" }}>Privacy</Link>
          <Link to="/terms-of-service" style={{ color: "#5B6B7C", textDecoration: "none", fontSize: "0.9rem" }}>Terms</Link>
        </div>
        <p style={{ color: "#8A93A2", fontSize: "0.85rem" }}>&copy; {new Date().getFullYear()} Cosmovex. All rights reserved.</p>
      </footer>

      {live && <StickyInstallBar app={app} accent={accent} />}
    </div>
  );
};

export default AppPromoPage;
